import React from 'react';

import type { ILead } from 'src/services/api';
import ExpandableItem from 'src/components/lead-sidebar/LeadSidebarExpandableItem';
import { FlexColumn } from 'src/components/flex';
import FactRow from 'src/components/lead-sidebar/FactRow';
import LeadFactsList from 'src/components/lead-sidebar/LeadFactsList';
import EditFactButton from 'src/components/lead-sidebar/EditFactButton';
import { useJustAddedKeys } from 'src/components/lead-sidebar/just-added-facts-context';
import { getDisplayedFacts } from 'src/components/lead-sidebar/displayed-lead-facts';

type Props = {
    lead: ILead;
};

/**
 * The first-class lead facts: contact facts plus the extracted fact columns, each
 * editable because every fact key has an accept/reject suggestion lifecycle, followed
 * by the (read-only) Reffie ID. The remaining smart fields live in the sibling Smart
 * Fields card.
 */
export default function LeadInfo({ lead }: Props) {
    const displayedUUID = lead.uuid.slice(0, 10) + '...';
    const hasFacts = getDisplayedFacts(lead).length > 0;

    const justAddedKeys = useJustAddedKeys();

    return (
        <ExpandableItem
            title="Lead Info"
            defaultExpanded={hasFacts || !!lead.phone_number || !!lead.email}
        >
            <FlexColumn rowGap={1.5} pb={1}>
                {lead.phone_number && (
                    <FactRow
                        label="Phone"
                        value={lead.phone_number}
                        factKey="phone"
                        isJustAdded={justAddedKeys.has('phone')}
                        editButton={
                            <EditFactButton
                                leadUuid={lead.uuid}
                                title="Phone"
                                testId="phone_number"
                                factKey="phone_number"
                                value={lead.phone_number}
                            />
                        }
                    />
                )}
                {lead.email && (
                    <FactRow
                        label="Email"
                        value={lead.email}
                        factKey="email"
                        isJustAdded={justAddedKeys.has('email')}
                        editButton={
                            <EditFactButton
                                leadUuid={lead.uuid}
                                title="Email"
                                testId="email"
                                factKey="email"
                                value={lead.email}
                            />
                        }
                    />
                )}
                <LeadFactsList lead={lead} />
                <FactRow
                    label="Reffie ID"
                    value={displayedUUID}
                    copyText={lead.uuid}
                    factKey="reffie-id"
                />
            </FlexColumn>
        </ExpandableItem>
    );
}
